/**
 * SportsClubDetail — /sports/clubs/:slug
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Users, MapPin, Phone, Mail, Globe, Loader2, Video, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import SportsCategoryNav from "@/components/SportsCategoryNav";
import VideoEmbed from "@/components/VideoEmbed";

export default function SportsClubDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    http.get(`/sports/clubs/${slug}`).then(r => setC(r.data)).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) {
    return (
      <>
        <SportsCategoryNav />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <h1 className="font-display text-2xl mb-2">Club not found</h1>
          <p className="opacity-70">{err}</p>
          <Link to="/sports/clubs" className="wofy-link inline-block mt-4">← Back to clubs</Link>
        </div>
      </>
    );
  }
  if (!c) return <><SportsCategoryNav /><div className="max-w-3xl mx-auto px-4 py-12 opacity-60 inline-flex gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div></>;

  return (
    <>
      <SportsCategoryNav />
      <article className="max-w-4xl mx-auto px-4 py-8" data-testid="club-detail">
        {c.hero_image_url && (
          <div className="w-full h-56 sm:h-72 rounded-xl overflow-hidden mb-6 bg-black/5"
               style={{ backgroundImage: `url(${c.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}

        <div className="flex items-start gap-4 mb-4">
          {c.logo_url ? (
            <img src={c.logo_url} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-white" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-black/5 grid place-items-center text-xl font-mono">
              {(c.name || "?")[0].toUpperCase()}
            </div>
          )}
          <div>
            <div className="overline mb-1"><Users className="w-3 h-3 inline mr-1" />{c.sport}</div>
            <h1 className="font-display text-4xl leading-tight">{c.name}</h1>
            {(c.city || c.country) && (
              <div className="text-sm opacity-70 flex items-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5" />{[c.city, c.state, c.country].filter(Boolean).join(", ")}
                {c.founded_year && <span className="ml-2 opacity-60">· Founded {c.founded_year}</span>}
              </div>
            )}
          </div>
        </div>

        {c.description && (
          <p className="whitespace-pre-line leading-relaxed opacity-90 mb-6">{c.description}</p>
        )}

        {/* Instant website CTA — visible to everyone but owner-scoped on submit */}
        <div className="wofy-card !p-3 mb-6 flex items-center gap-3 flex-wrap"
             style={{ background: "var(--wofy-surface)" }} data-testid="website-cta">
          <Sparkles className="w-5 h-5 opacity-70" />
          <div className="text-sm flex-1 min-w-0">
            <b>Instant Website for this club</b>
            <div className="text-xs opacity-70">Auto-fill from this profile, pick a template, publish in minutes.</div>
          </div>
          <Link to={`/websites/new?source_type=sports_club&source_id=${c.id}`}
            className="wofy-btn-primary text-xs inline-flex items-center gap-1"
            data-testid="cta-instant-website">
            <Sparkles className="w-3.5 h-3.5" />Get a free website
          </Link>
        </div>

        {c.sports_secondary?.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2">Also plays</h2>
            <div className="flex flex-wrap gap-2">
              {c.sports_secondary.map(s => (
                <span key={s} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--wofy-border)" }}>{s}</span>
              ))}
            </div>
          </section>
        )}

        {c.video_links?.length > 0 && (
          <section className="mb-8" data-testid="club-videos">
            <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2">
              <Video className="w-5 h-5" />Videos
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {c.video_links.map((v, i) => <VideoEmbed key={i} video={v} />)}
            </div>
          </section>
        )}

        {c.photo_urls?.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-3">Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {c.photo_urls.map((p, i) => (
                <img key={i} src={p} alt="" className="w-full aspect-square object-cover rounded-lg" />
              ))}
            </div>
          </section>
        )}

        {(c.contact_email || c.contact_phone || c.website) && (
          <section className="wofy-card !p-4">
            <div className="overline mb-1">CONTACT</div>
            <div className="text-sm opacity-80 mt-1 flex flex-wrap gap-3">
              {c.contact_email && <a href={`mailto:${c.contact_email}`} className="inline-flex items-center gap-1 wofy-link"><Mail className="w-3.5 h-3.5" />{c.contact_email}</a>}
              {c.contact_phone && <a href={`tel:${c.contact_phone}`} className="inline-flex items-center gap-1 wofy-link"><Phone className="w-3.5 h-3.5" />{c.contact_phone}</a>}
              {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 wofy-link"><Globe className="w-3.5 h-3.5" />Website</a>}
            </div>
          </section>
        )}
      </article>
    </>
  );
}
